// ── Canvas dimensions ─────────────────────────────────────────────────────────
export const CANVAS_W = 1440;
export const CANVAS_H = 810;
export const ASPECT   = CANVAS_H / CANVAS_W;

// ── Brand color ───────────────────────────────────────────────────────────────
export const ENG_PRIMARY = '#1075BA';

// ── Modal widths ──────────────────────────────────────────────────────────────
export const MODAL_WIDTHS = { sm: 480, md: 720, lg: 960, xl: 1200, full: '100%' };

// ── Chat UI ───────────────────────────────────────────────────────────────────
export const CHAT_W    = 328;
export const CHAT_H    = 440;
export const CHAT_BLUE = '#0a7aff';

// ── Hotspot SVG keyframe string (injected into <style> inside SVG defs) ───────
export const HOTSPOT_KEYFRAMES = `
  @keyframes hs-pulse { 0% { r: 10.5; } 50% { r: 13; } 100% { r: 10.5; } }
  @keyframes hs-ring  { 0% { r: 10.5; opacity: 0.6; } 100% { r: 28; opacity: 0; } }
  @keyframes hs-shape-shimmer { 0% { opacity: 0; } 20% { opacity: 0.18; } 60% { opacity: 0.18; } 100% { opacity: 0; } }
`;

// ── Responsive breakpoints ────────────────────────────────────────────────────
// Matches the builder breakpoint definitions. Used to pick the right bg and
// hotspot geometry override at runtime based on actual screen width.
// canvasW / canvasH mirror the builder's LOBBY_BREAKPOINTS dimensions so the
// engine scales the canvas to the correct aspect ratio for each breakpoint.
export const ENG_BREAKPOINTS = [
  { key: 'mobile',           maxW: 480,  canvasW: 375,  canvasH: 700  },
  { key: 'tablet-portrait',  maxW: 820,  canvasW: 768,  canvasH: 1024 },
  { key: 'tablet-landscape', maxW: 1180, canvasW: 1024, canvasH: 768  },
  // desktop = everything wider than 1180
];

/**
 * Returns the active responsive breakpoint key for a given screen width.
 * Returns 'desktop' when the screen is wider than all breakpoint thresholds.
 */
export function getEngBreakpoint(screenW) {
  for (const bp of ENG_BREAKPOINTS) {
    if (screenW <= bp.maxW) return bp.key;
  }
  return 'desktop';
}

/**
 * Returns { canvasW, canvasH } for the given breakpoint key.
 * Falls back to the desktop dimensions (1440×810) for 'desktop' or unknown keys.
 */
export function getEngCanvasSize(bpKey) {
  const spec = ENG_BREAKPOINTS.find(b => b.key === bpKey);
  return spec ? { canvasW: spec.canvasW, canvasH: spec.canvasH } : { canvasW: CANVAS_W, canvasH: CANVAS_H };
}

/**
 * Resolve the effective hotspot geometry for a breakpoint.
 * Falls back to desktop values when no override exists.
 */
export function resolveEngHotspot(hotspot, bp) {
  if (!bp || bp === 'desktop') return hotspot;
  const override = hotspot?.responsive?.[bp];
  if (!override) return hotspot;

  // Merge geometry — treat empty points array as "not overridden" and fall back to desktop
  const resolved = {
    ...hotspot,
    points: (override.points && override.points.length > 0) ? override.points : hotspot.points,
    meta:   { ...(hotspot.meta || {}), ...(override.meta || {}) },
  };

  // Merge bp-level style fields onto the top-level properties that renderers read
  const style = override.style;
  if (style) {
    if (style.hoverFillColor     !== undefined) resolved.hoverFillColor     = style.hoverFillColor;
    if (style.hoverFillOpacity   !== undefined) resolved.hoverFillOpacity   = style.hoverFillOpacity;
    if (style.hoverBorderColor   !== undefined) resolved.hoverBorderColor   = style.hoverBorderColor;
    if (style.hoverBorderOpacity !== undefined) resolved.hoverBorderOpacity = style.hoverBorderOpacity;
    if (style.strokeWidth        !== undefined) resolved.strokeWidth        = style.strokeWidth;
  }

  // Merge bp-level transition (only when explicitly set — undefined means "no override")
  if ('transitionVideoUrl' in override) {
    resolved.hotspotAction = {
      ...(hotspot.hotspotAction || {}),
      transitionVideoUrl: override.transitionVideoUrl,
    };
  }

  return resolved;
}

/**
 * Returns the effective bg url and type for a lobby + breakpoint.
 * Falls back to desktop values when no responsive override exists.
 */
export function resolveEngBg(lobby, bp) {
  if (!bp || bp === 'desktop' || !lobby) {
    return { bgUrl: lobby?.bgUrl || null, bgType: lobby?.bgType || 'image' };
  }
  const bpBg = lobby.responsive?.[bp] || {};
  return {
    bgUrl:  bpBg.bgUrl  !== undefined ? bpBg.bgUrl  : (lobby.bgUrl  || null),
    bgType: bpBg.bgType !== undefined ? bpBg.bgType : (lobby.bgType || 'image'),
  };
}